import React from 'react';

import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { useTwcg2304ImportInOnboarding } from '@confluence/experiment-twcg-2304-import-in-onboarding/entry-points/useTwcg2304ImportInOnboarding.ts';

import {
	IMPORT_STRATEGIES,
	MARKDOWN_IMPORT_STRATEGY,
	type ImportStrategy,
} from './importStrategies.tsx';
import { ActiveImports } from './importStrategyDefinitions.ts';

export type ImportStrategySurface = 'editorRightRail' | 'onboarding';

type ImportStrategyRegistryProps = {
	surface: ImportStrategySurface;
	contentId: string;
	spaceId: string | null;
	hasSourceRestrictions: boolean;
	children: (strategies: ImportStrategy[]) => React.ReactNode;
};

const FEDRAMP_HOST_SUFFIXES = [
	'atlassian-fex.com',
	'atlassian-fex.net',
	'atlassian-us-gov-mod.com',
	'atlassian-us-gov-mod.net',
	'atlassian-stg-fedm.net',
] as const;

const FEDRAMP_IMPORT_STRATEGIES = IMPORT_STRATEGIES.filter(
	({ key }) => key === ActiveImports.IMPORT_FROM_LOCAL,
);

const isFedRampHostname = (hostname: string): boolean =>
	FEDRAMP_HOST_SUFFIXES.some((suffix) => hostname === suffix || hostname.endsWith(`.${suffix}`));

export const ImportStrategyRegistry = ({
	surface,
	contentId,
	spaceId,
	hasSourceRestrictions,
	children,
}: ImportStrategyRegistryProps): React.JSX.Element => {
	const hostname = globalThis.location?.hostname ?? '';
	const isFedRampEnvironment = isFedRampHostname(hostname);
	const hasEligibleDestination =
		Boolean(contentId) && Boolean(spaceId && /^\d+$/.test(spaceId)) && !hasSourceRestrictions;
	const { isEnabled: isTwcg2304Treatment, loading: isTwcg2304Loading } =
		useTwcg2304ImportInOnboarding({
			skip: surface !== 'editorRightRail' || !hasEligibleDestination || isFedRampEnvironment,
		});

	const baseStrategies =
		isFedRampEnvironment && fg('cc_feature_discovery_ifs_fe')
			? FEDRAMP_IMPORT_STRATEGIES
			: IMPORT_STRATEGIES;

	return (
		<>
			{children([
				...baseStrategies,
				...(surface === 'editorRightRail' &&
				hasEligibleDestination &&
				!isFedRampEnvironment &&
				!isTwcg2304Loading &&
				!isTwcg2304Treatment &&
				expValEquals('twcg_2627_markdown_file_import_into_confluence', 'isEnabled', true)
					? [MARKDOWN_IMPORT_STRATEGY]
					: []),
			])}
		</>
	);
};
